import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCOP, formatDate, formatApiError } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { PaymentBadge, ReviewBadge } from "../components/StatusBadges";

export default function ReviewerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/reviewer/cases?limit=50");
        if (alive) setData(data);
      } catch (e) {
        if (alive) setError(formatApiError(e?.response?.data?.detail));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="reviewer-dashboard" className="container-prose py-12">
      <div className="mb-10 pb-6 border-b border-gray-200">
        <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">Bandeja de revisión</p>
        <h1 className="font-serif text-3xl text-gray-900">Revisor · {user?.name}</h1>
        <p className="text-sm text-gray-600 mt-1">
          Casos asignados a tu revisión. Los botones de aprobación / rechazo se habilitarán en próximas fases.
        </p>
      </div>

      {loading && <div className="border border-gray-200 p-8 text-sm text-gray-500">Cargando casos asignados…</div>}

      {!loading && error && (
        <div className="border border-destructive/30 bg-destructive/5 text-destructive p-4 text-sm">{error}</div>
      )}

      {!loading && !error && data.items.length === 0 && (
        <div data-testid="reviewer-empty-state" className="border border-gray-200 p-12 text-center bg-gray-50">
          <p className="font-serif text-2xl text-gray-900 mb-2">No tienes casos asignados</p>
          <p className="text-sm text-gray-600 max-w-md mx-auto">
            El administrador te asignará casos a revisar cuando estén pagos y listos para diagnóstico o Kit Documental.
          </p>
        </div>
      )}

      {!loading && !error && data.items.length > 0 && (
        <div className="border border-gray-200 bg-white overflow-x-auto" data-testid="reviewer-cases-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left font-medium">Caso</th>
                <th className="px-3 py-3 text-left font-medium">Dueño</th>
                <th className="px-3 py-3 text-right font-medium whitespace-nowrap">Valor</th>
                <th className="px-3 py-3 text-left font-medium">Pago</th>
                <th className="px-3 py-3 text-left font-medium">Revisión</th>
                <th className="px-3 py-3 text-right font-medium whitespace-nowrap">Asignado</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.id} data-testid={`reviewer-case-row-${c.id}`} className="border-b border-gray-200 last:border-b-0 hover:bg-gray-50">
                  <td className="px-4 py-3 max-w-[280px]">
                    <Link to={`/reviewer/cases/${c.id}`} className="block">
                      <div className="font-medium text-gray-900 truncate">{c.title}</div>
                      <div className="text-xs text-gray-500 truncate">{c.objeto_contractual}</div>
                    </Link>
                  </td>
                  <td className="px-3 py-3 text-gray-700 max-w-[180px] truncate">{c.user_email || "—"}</td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">{formatCOP(c.valor_estimado_cop)}</td>
                  <td className="px-3 py-3"><PaymentBadge value={c.payment_status} /></td>
                  <td className="px-3 py-3"><ReviewBadge value={c.review_status} /></td>
                  <td className="px-3 py-3 text-right text-xs text-gray-500 whitespace-nowrap">{formatDate(c.assigned_at || c.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
